import * as React from 'react'
import { useEvent, useStore } from 'effector-react/ssr'
import { debounce } from 'debounce'
import styled from 'styled-components'
import loadable from '@loadable/component'
import * as model from '@pages/live/model'
import { Layout } from '@ui/layout'
import liveCover from '@assets/images/live-cover.jpg'
import liveCoverMobile from '@assets/images/live-cover-mobile.jpg'
import { matchMedia, MediaSource } from '@lib/image-media'
import { Devices } from '@styles'
import { isClientSide } from '@lib/ssr'
import { $isMobile } from '@features/window-size-observer'
import { useBackgroundOnKroll } from '@lib/use-on-krol'
import { VideoPreview } from './video-preview'

const liveMedia: MediaSource<string>[] = [
  { mediaQuery: Devices.laptop, source: liveCover },
  { mediaQuery: Devices.mobileS, source: liveCoverMobile },
]

const videoTitleMedia: MediaSource<number>[] = [
  { mediaQuery: Devices.desktopM, source: 100 },
  { mediaQuery: Devices.desktopS, source: 80 },
  { mediaQuery: Devices.laptop, source: 70 },
  { mediaQuery: Devices.mobileL, source: 50 },
  { mediaQuery: Devices.mobileM, source: 30 },
  { mediaQuery: Devices.mobileS, source: 10 },
]

const DesktopPlayer = loadable(() => import('@features/video-player/desktop-player'), {
  resolveComponent: (module) => module.DesktopVideoPlayer,
  ssr: false,
})

const MobilePlayer = loadable(() => import('@features/video-player/mobile-player'), {
  resolveComponent: (module) => module.MobileVideoPlayer,
  ssr: false,
})

export const LivePage = () => {
  const [pageCover, setPageCover] = React.useState('')
  const showBackground = useBackgroundOnKroll()
  const [videoTitleLength, setVideoTitleLength] = React.useState(50)

  const isMobile = useStore($isMobile)
  const selectedVideoId = useStore(model.$selectedVideoId)
  const video = useStore(model.$selectedVideo)
  const videos = useStore(model.$videos)
  const videoChanged = useEvent(model.selectedVideoChanged)
  const onUnmount = useEvent(model.resetSelectedVideo)

  const imageUrl = `url(${pageCover})`

  const onResize = React.useCallback(
    debounce(() => {
      setPageCover(matchMedia(liveMedia, ''))
      setVideoTitleLength(matchMedia(videoTitleMedia, 50))
    }, 100),
    [],
  )

  React.useEffect(() => {
    if (isClientSide()) {
      setPageCover(matchMedia(liveMedia, ''))
      setVideoTitleLength(matchMedia(videoTitleMedia, 50))

      window.addEventListener('resize', onResize)
    }

    return () => {
      if (isClientSide()) {
        window.removeEventListener('resize', onResize)
      }
    }
  }, [])

  return (
    <Layout showBorder={showBackground}>
      <LiveCover style={{ backgroundImage: imageUrl }} />

      <PageTitle>LIVE</PageTitle>

      <LivePageWrapper>
        <div>
          {video !== null && !isMobile && <DesktopPlayer source={video} onUnmount={onUnmount} />}
          {isMobile && <MobilePlayer source={video} onUnmount={onUnmount} />}
          {video !== null && <VideoInformation date={video.date} title={video.title} description={video.description} />}
        </div>

        <div>
          {videos.map((v) => (
            <VideoPreview
              key={v.id}
              url={v.url}
              title={v.title}
              date={v.date}
              videoTitleLength={videoTitleLength}
              isSelected={v.id === selectedVideoId}
              onClick={() => videoChanged(v.id)}
            />
          ))}
        </div>
      </LivePageWrapper>
    </Layout>
  )
}

const LivePageWrapper = styled.div`
  display: flex;
  flex-direction: column;

  padding: 0 25px;

  @media screen and ${Devices.laptop} {
    display: grid;
    align-content: center;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    padding: 10px 75px 0 75px;
  }

  @media screen and ${Devices.desktopS} {
    display: grid;
    align-content: center;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    padding: 10px 75px 0 75px;
  }
`

const LiveCover = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: -1;

  width: 100vw;
  height: 100vh;

  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
`

const PageTitle = styled.h1`
  font-family: 'Magnolia Script', 'Sans Serif';
  font-style: normal;
  font-weight: normal;
  font-size: 30px;
  line-height: 40px;

  margin: 0;

  margin-top: 20px;
  margin-bottom: 20px;
  margin-left: 25px;

  @media screen and ${Devices.laptop} {
    display: none;
  }
`

interface VideoInformationProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string
  date: Date
  description: string
}

const VideoInformation = (props: VideoInformationProps) => {
  return (
    <Information className={props.className} style={props.style}>
      <Date>{props.date.toLocaleDateString()}</Date>
      <Title>{props.title}</Title>
      <Description>{props.description}</Description>
    </Information>
  )
}

const Information = styled.div`
  display: none;

  flex-direction: column;

  background-color: rgba(228, 105, 209, 0.1);

  padding: 30px;
  margin-bottom: 20px;

  @media screen and ${Devices.laptop} {
    display: flex;
  }

  @media screen and ${Devices.desktopS} {
    margin-bottom: unset;
  }
`

const Date = styled.span`
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;

  margin-bottom: 10px;
`

const Title = styled.span`
  font-weight: 700;
  font-size: 18px;
  line-height: 21px;

  margin-bottom: 20px;
`

const Description = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 400;
`
